/**
 * Cells the tab audit says disagree - with another tab, or with the filing.
 *
 * The audit (v2_tab_audit, on the Overview) names each failing check's computed series and
 * period. A row does not know which series it shows, only the object it was handed, so the
 * flags are keyed by that object: the provider resolves each check's dotted path into the
 * model's computed figures, and a row looks its own `data` up. Empty by default, so v1 and
 * any table outside a provider render exactly as before.
 */

import { createContext, useContext, type ReactNode } from 'react';

/** Series object -> period -> why the cell is flagged. */
export type CheckFlags = Map<object, Map<string, string>>;

const CheckFlagsContext = createContext<CheckFlags>(new Map());

export interface FlaggableCheck {
  field?: string;
  period?: string;
  line: string;
  model: number;
  against: number;
  source: string;
}

function resolve(root: unknown, path: string): unknown {
  let node: unknown = root;
  for (const part of path.split('.')) {
    if (node == null || typeof node !== 'object') return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  return node;
}

/** Each failing check's series in `computed`, with the period and the reason. */
export function buildCheckFlags(computed: unknown, failing: FlaggableCheck[]): CheckFlags {
  const flags: CheckFlags = new Map();
  for (const check of failing) {
    if (!check.field || !check.period) continue;
    const series = resolve(computed, check.field);
    if (!series || typeof series !== 'object') continue;
    const periods = flags.get(series) ?? new Map<string, string>();
    const reason = `${check.line}: ${check.model.toLocaleString()} here, ${check.against.toLocaleString()} (${check.source})`;
    periods.set(check.period, periods.has(check.period) ? `${periods.get(check.period)}\n${reason}` : reason);
    flags.set(series, periods);
  }
  return flags;
}

/** A figure the filing does not vouch for: its cell, and the question that settles it. */
export interface FlaggableFigure {
  status: string;
  field: string;
  period: string;
  question: string;
}

/**
 * The same, for figures no printed total vouches for (v2_figure_status): marked in their
 * own cell, with the question as the reason. Adds to `flags`; a cell a check already flags
 * keeps both reasons.
 */
export function addFigureFlags(computed: unknown, flags: CheckFlags, figures: FlaggableFigure[]): CheckFlags {
  for (const figure of figures) {
    if (figure.status !== 'unverified' || !figure.field || !figure.period) continue;
    const series = resolve(computed, figure.field);
    if (!series || typeof series !== 'object') continue;
    const periods = flags.get(series) ?? new Map<string, string>();
    const before = periods.get(figure.period);
    periods.set(figure.period, before ? `${before}\n${figure.question}` : figure.question);
    flags.set(series, periods);
  }
  return flags;
}

export function CheckFlagsProvider({ flags, children }: { flags: CheckFlags; children: ReactNode }) {
  return <CheckFlagsContext.Provider value={flags}>{children}</CheckFlagsContext.Provider>;
}

/** Why this cell of this series is flagged, if it is. */
export function useCheckFlags(): CheckFlags {
  return useContext(CheckFlagsContext);
}
